import { useEffect, useRef } from 'react';
import { cn } from '../lib/utils';

export interface TrustpilotStandardWidgetProps {
  className?: string;
  isMobileOnly?: boolean;
  isDesktopOnly?: boolean;
  alignment?: 'left' | 'center' | 'right';
}

/**
 * TrustpilotStandardWidget: Official iframe-based "Review Collector" widget.
 * Uses the exact HTML provided by Trustpilot for client review.
 */
export function TrustpilotStandardWidget({ 
  className, 
  isMobileOnly, 
  isDesktopOnly,
  alignment = 'left'
}: TrustpilotStandardWidgetProps) {
  const desktopRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        // Dynamically inject Trustpilot script if it doesn't exist to avoid Lighthouse cache warnings on initial load
        const scriptId = 'trustpilot-bootstrap-script';
        let script = document.getElementById(scriptId) as HTMLScriptElement;

        if (!script) {
          script = document.createElement('script');
          script.id = scriptId;
          script.type = 'text/javascript';
          script.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
          script.async = true;
          document.head.appendChild(script);
        }

        // Wait for script to evaluate and initialize
        const initTrustpilot = () => {
          if ((window as any).Trustpilot) {
            if (desktopRef.current) (window as any).Trustpilot.loadFromElement(desktopRef.current, true);
            if (mobileRef.current) (window as any).Trustpilot.loadFromElement(mobileRef.current, true);
          } else {
            setTimeout(initTrustpilot, 300);
          }
        };
        initTrustpilot();
        observer.disconnect();
      }
    }, { rootMargin: '200px' });

    if (desktopRef.current) observer.observe(desktopRef.current);
    if (mobileRef.current) observer.observe(mobileRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className={cn("trustpilot-standard-widget-wrapper", className)}>
      {/* Desktop Widget */}
      {!isMobileOnly && (
        <div
          ref={desktopRef}
          className={cn("trustpilot-widget", !isDesktopOnly && "hidden md:block")}
          data-locale="en-US"
          data-template-id="56278e9abfbbba0bdcd568bc"
          data-businessunit-id="697d34bbce0741757faeb0d7"
          data-style-height="52px"
          data-style-width="100%"
          style={{ minHeight: '52px' }}
          data-alignment={alignment}
          data-token="f4576129-3781-455c-b947-536910b266db"
        >
          <a href="https://www.trustpilot.com/review/shopmuchmore.co.uk" target="_blank" rel="noopener">
            Trustpilot
          </a>
        </div>
      )}

      {/* Mobile Widget */}
      {!isDesktopOnly && (
        <div
          ref={mobileRef}
          className={cn("trustpilot-widget", !isMobileOnly && "md:hidden")}
          data-locale="en-US"
          data-template-id="56278e9abfbbba0bdcd568bc"
          data-businessunit-id="697d34bbce0741757faeb0d7"
          data-style-height="52px"
          data-style-width="100%"
          style={{ minHeight: '52px' }}
          data-alignment={alignment}
          data-token="1cf5f13b-6ae2-4e1a-8e9e-61714d1b64fb"
        >
          <a href="https://www.trustpilot.com/review/shopmuchmore.co.uk" target="_blank" rel="noopener">
            Trustpilot
          </a>
        </div>
      )}
    </div>
  );
}
